import { useEffect, useState, type ReactNode } from "react";
import { Button, Input, Spin, Tabs, Typography } from "@arco-design/web-react";

import { canUsePermission } from "../../../lib/auth";
import { isHttpClientError } from "../../../lib/http/errors";
import { useCurrentAuthUser } from "../../auth";
import type { OpportunityCustomizationProjectItem } from "../../crm_opportunity_workbench/types";
import {
  fetchCustomizationProjectHistory,
  fetchCustomizationProjectDemandVersions,
} from "../repo/customizationRepo";
import { createCustomizationFromLinkedProject } from "../runtime/createCustomizationFromLinkedProject";
import { createCustomizationInitialDemandVersion } from "../runtime/createCustomizationInitialDemandVersion";
import { createCustomizationProjectWithTrace } from "../runtime/createCustomizationProjectWithTrace";
import {
  isCustomizationMobileDevice,
  openPendingCustomizationWorkbenchWindow,
} from "../runtime/customizationDevice";
import { fetchCustomizationOpportunityContext } from "../runtime/fetchCustomizationOpportunityContext";
import { resolveHistoryCandidateTravelDate } from "../runtime/filterHistoryCustomizationCandidates";
import {
  isOpportunityLinkedProjectCopyable,
  resolveOpportunityAddCustomizationDefaultTab,
  resolveOpportunityAddCustomizationTabOrder,
  type OpportunityAddCustomizationTabKey,
} from "../service/customizationService";
import type {
  CustomizationOpportunityLinkedProjectItem,
  CustomizationProjectListItem,
  CustomizationTraceCreate,
} from "../types";
import "./CustomizationNewProjectTraceDraft.css";

const { TextArea } = Input;
const { Text } = Typography;
const TabPane = Tabs.TabPane;

/** 与三栏堆叠断点一致：≤1100px 改用顶栏 Tab。 */
const TRIPTYCH_TABS_MAX_WIDTH = 1100;

const HISTORY_SEARCH_DEBOUNCE_MS = 300;
const HISTORY_SEARCH_PAGE_SIZE = 20;

const NEW_PROJECT_TRACE_EXAMPLE =
  "清华大学，客户是壁画专业，联系人是向老师，壁画专业，需要一条福建的线路，厦门需要加上鼓浪屿，主要考察南洋和侨乡艺术内容，2026年10月12日出发，共计12天，99学生，4带队老师";

type NewProjectSubmitPhase =
  | "idle"
  | "creating_project"
  | "generating_version"
  | "copying_project";

type Props = {
  opportunityId?: number;
  /** 嵌入商机抽屉时去掉全页壳层样式。 */
  embedded?: boolean;
  onCreated: (
    projectId: string,
    options?: { pendingWindow?: Window | null },
  ) => void;
};

/** 新项目本地临时草稿：提交第一条追溯后才正式建项。 */
export function CustomizationNewProjectTraceDraft({
  opportunityId,
  embedded = false,
  onCreated,
}: Props) {
  const currentUser = useCurrentAuthUser().data;
  const canSearchHistory = canUsePermission(
    currentUser,
    "customization.project:history_search",
  );
  const canCreateProject = canUsePermission(currentUser, "customization.project:create");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<NewProjectSubmitPhase>("idle");
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [bootstrapLoading, setBootstrapLoading] = useState(Boolean(opportunityId));
  const [contextError, setContextError] = useState("");
  const [linkedProjects, setLinkedProjects] = useState<CustomizationOpportunityLinkedProjectItem[]>(
    [],
  );
  const [opportunityIdentity, setOpportunityIdentity] = useState<{
    orgId?: number;
    contactId?: number;
  }>({});
  const [linkedError, setLinkedError] = useState("");
  const [activeTriptychTab, setActiveTriptychTab] =
    useState<OpportunityAddCustomizationTabKey>("create");
  const [triptychTabReady, setTriptychTabReady] = useState(false);
  const isCompactTriptych = useIsCompactTriptychViewport(TRIPTYCH_TABS_MAX_WIDTH);
  const normalizedContent = content.trim();
  const showTriptych = Boolean(opportunityId);
  const useTriptychTabs = showTriptych && isCompactTriptych;
  const hasLinkedProjects = linkedProjects.length > 0;
  const triptychTabOrder = resolveOpportunityAddCustomizationTabOrder(hasLinkedProjects);
  const rootClassName = [
    "customization-new-project-draft-page",
    embedded ? "customization-new-project-draft-page--embedded" : "",
    showTriptych ? "customization-new-project-draft-page--triptych" : "",
    useTriptychTabs ? "customization-new-project-draft-page--tabs" : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    setTriptychTabReady(false);
    setActiveTriptychTab("create");
  }, [opportunityId]);

  useEffect(() => {
    if (!opportunityId) {
      return;
    }
    let cancelled = false;
    setBootstrapLoading(true);
    setContextError("");
    setLinkedError("");
    void fetchCustomizationOpportunityContext(opportunityId)
      .then((context) => {
        if (cancelled) {
          return;
        }
        if (context.initialTraceContent) {
          setContent((current) => current || context.initialTraceContent);
        }
        setOpportunityIdentity({ orgId: context.orgId, contactId: context.contactId });
        setLinkedProjects(context.linkedProjects);
        setActiveTriptychTab(
          resolveOpportunityAddCustomizationDefaultTab(context.linkedProjects.length > 0),
        );
        setTriptychTabReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setOpportunityIdentity({});
          setContextError("商机信息加载失败，暂时无法创建关联定制。");
          setLinkedError("商机已有定制加载失败，仍可继续录入新追溯。");
          setLinkedProjects([]);
          setActiveTriptychTab("create");
          setTriptychTabReady(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBootstrapLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [opportunityId]);

  const submitTrace = async (traceContent: string) => {
    const normalized = traceContent.trim();
    if (!normalized) {
      setErrorMessage("请先填写第一条有效需求追溯。");
      return;
    }
    const payload: CustomizationTraceCreate = {
      role: "sales",
      sourceType: "phone",
      content: normalized,
      isImportant: false,
      ...(opportunityId ? { opportunityId } : {}),
    };
    setSubmitting(true);
    setSubmitPhase("creating_project");
    setErrorMessage("");
    // 桌面商机抽屉：点击同步阶段先开过渡窗；手机端同页进入，不预开新窗。
    const pendingWindow =
      embedded && !isCustomizationMobileDevice()
        ? openPendingCustomizationWorkbenchWindow()
        : null;
    try {
      const result = await createCustomizationProjectWithTrace(payload);
      setSubmitPhase("generating_version");
      try {
        await createCustomizationInitialDemandVersion(result.project.id, result.trace.id);
      } catch {
        onCreated(result.project.id, { pendingWindow });
        return;
      }
      onCreated(result.project.id, { pendingWindow });
    } catch {
      pendingWindow?.close();
      setSubmitPhase("idle");
      setErrorMessage("提交追溯失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    await submitTrace(content);
  };

  const handleUseExample = () => {
    setContent(NEW_PROJECT_TRACE_EXAMPLE);
    setErrorMessage("");
  };

  const handleCopyProject = async (project: CustomizationOpportunityLinkedProjectItem) => {
    if (submitting || !isOpportunityLinkedProjectCopyable(project)) {
      return;
    }
    if (!opportunityId) {
      return;
    }
    if (!opportunityIdentity.orgId || !opportunityIdentity.contactId) {
      setErrorMessage("需要商机设置了学校和联系人才能复制创建");
      return;
    }
    const baseDemandId = project.effectiveDemandId || project.currentDemandId;
    if (!baseDemandId) {
      setErrorMessage("该定制尚无需求版本，无法复制创建新定制。");
      return;
    }
    setSubmitting(true);
    setSubmitPhase("copying_project");
    setCopyingId(project.projectId);
    setErrorMessage("");
    const pendingWindow =
      embedded && !isCustomizationMobileDevice()
        ? openPendingCustomizationWorkbenchWindow()
        : null;
    try {
      const result = await createCustomizationFromLinkedProject(
        project,
        opportunityId,
        "opportunity_linked",
      );
      onCreated(result.project.id, { pendingWindow });
    } catch (error) {
      pendingWindow?.close();
      setSubmitPhase("idle");
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "复制，创建新定制失败，请稍后重试。",
      );
    } finally {
      setCopyingId(null);
      setSubmitting(false);
    }
  };

  const handleCopyHistoryProject = async (item: CustomizationProjectListItem) => {
    if (submitting) {
      return;
    }
    if (!opportunityIdentity.orgId || !opportunityIdentity.contactId) {
      setErrorMessage("需要商机设置了学校和联系人才能复制创建");
      return;
    }
    setSubmitting(true);
    setSubmitPhase("copying_project");
    setCopyingId(item.id);
    setErrorMessage("");
    const pendingWindow =
      embedded && !isCustomizationMobileDevice()
        ? openPendingCustomizationWorkbenchWindow()
        : null;
    try {
      const versions = await fetchCustomizationProjectDemandVersions(item.id);
      const demand = versions.items[0];
      if (!demand) {
        throw new Error("该定制尚无需求版本，无法复制创建新定制。");
      }
      const result = await createCustomizationFromLinkedProject(
        mapHistoryListItemToLinkedProject(item, demand.id, demand.versionNumber),
        opportunityId,
        "opportunity_history",
      );
      onCreated(result.project.id, { pendingWindow });
    } catch (error) {
      pendingWindow?.close();
      setSubmitPhase("idle");
      const resolvedErrorMessage = resolveHistoryCopyErrorMessage(error);
      setErrorMessage(
        resolvedErrorMessage
          ? resolvedErrorMessage
          : "复制，创建新定制失败，请稍后重试。",
      );
    } finally {
      setCopyingId(null);
      setSubmitting(false);
    }
  };

  const opportunityBootstrapPending = Boolean(opportunityId) && bootstrapLoading;
  if (opportunityBootstrapPending) {
    return (
      <main
        className={[
          rootClassName,
          "customization-new-project-draft-page--bootstrapping",
          "customization-new-project-draft-page--triptych",
          isCompactTriptych ? "customization-new-project-draft-page--tabs" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label="商机增定制准备中"
        aria-busy="true"
      >
        <div className="customization-new-project-bootstrap-status" role="status">
          <Spin size={18} />
          <div>
            <strong>正在准备增定制</strong>
            <span>加载本商机已有定制与录入区，历史栏可稍后搜索对照</span>
          </div>
        </div>
        {isCompactTriptych ? (
          <div className="customization-new-project-tabs-skeleton" aria-hidden>
            <div className="customization-new-project-tabs-skeleton-nav">
              <span />
              <span />
              <span />
            </div>
            <OpportunityBootstrapColumnSkeleton title="加载中" lines={4} accent />
          </div>
        ) : (
          <>
            <OpportunityBootstrapColumnSkeleton title="历史定制" lines={3} />
            <OpportunityBootstrapColumnSkeleton title="本商机已有定制" lines={2} />
            <OpportunityBootstrapColumnSkeleton title="新增定制" lines={4} accent />
          </>
        )}
      </main>
    );
  }

  if (showTriptych) {
    const historyColumn = (
      <OpportunityHistoryCustomizationColumn
        opportunityId={opportunityId!}
        enabled={canSearchHistory}
        disabled={submitting || !canCreateProject}
        copyingId={copyingId}
        onCopy={handleCopyHistoryProject}
        hideHeader={useTriptychTabs}
      />
    );
    const linkedColumn = (
      <OpportunityLinkedCustomizationSection
        items={linkedProjects}
        errorMessage={linkedError}
        disabled={submitting || !canCreateProject}
        copyingId={copyingId}
        onCopy={handleCopyProject}
        columnMode
        hideHeader={useTriptychTabs}
      />
    );
    const createColumn = (
      <NewCustomizationColumn
        content={content}
        submitting={submitting}
        submitPhase={submitPhase}
        contextError={contextError}
        errorMessage={errorMessage}
        normalizedContent={normalizedContent}
        disabled={!canCreateProject}
        hideHeader={useTriptychTabs}
        onContentChange={setContent}
        onUseExample={handleUseExample}
        onSubmit={handleSubmit}
      />
    );
    const tabPanels: Record<OpportunityAddCustomizationTabKey, ReactNode> = {
      linked: linkedColumn,
      create: createColumn,
      history: historyColumn,
    };
    const tabTitles: Record<OpportunityAddCustomizationTabKey, string> = {
      linked: hasLinkedProjects ? `本商机已有定制 (${linkedProjects.length})` : "本商机已有定制",
      create: "新增定制",
      history: "历史定制",
    };

    if (useTriptychTabs) {
      return (
        <main className={rootClassName} aria-label="商机增定制三栏">
          {submitPhase === "copying_project" ? <CopyingProjectBanner /> : null}
          <Tabs
            activeTab={activeTriptychTab}
            onChange={(key) => setActiveTriptychTab(key as OpportunityAddCustomizationTabKey)}
            className="customization-new-project-triptych-tabs"
            size="small"
            destroyOnHide={false}
            aria-label={triptychTabReady ? "增定制分类" : "增定制分类加载中"}
          >
            {triptychTabOrder.map((tabKey) => (
              <TabPane key={tabKey} title={tabTitles[tabKey]}>
                {tabPanels[tabKey]}
              </TabPane>
            ))}
          </Tabs>
        </main>
      );
    }

    return (
      <main className={rootClassName} aria-label="商机增定制三栏">
        {submitPhase === "copying_project" ? <CopyingProjectBanner /> : null}
        {historyColumn}
        {linkedColumn}
        {createColumn}
      </main>
    );
  }

  return (
    <main className={rootClassName} aria-label="新定制本地临时草稿">
      <section className="customization-new-project-draft-panel">
        <NewCustomizationIntro />
        <div className="customization-new-project-draft-example" aria-label="本地临时参考示例">
          <div className="customization-new-project-draft-example-header">
            <span>参考示例</span>
            <Button type="text" size="mini" disabled={!canCreateProject || submitting} onClick={handleUseExample}>
              填入示例
            </Button>
          </div>
          <p>{NEW_PROJECT_TRACE_EXAMPLE}</p>
        </div>
        <TextArea
          aria-label="需求追溯内容"
          value={content}
          autoSize={{ minRows: 7, maxRows: 10 }}
          placeholder="填写客户、老师或销售确认的第一条需求追溯"
          disabled={!canCreateProject || submitting || Boolean(contextError)}
          onChange={setContent}
        />
        {submitting ? <NewProjectSubmitProgress phase={submitPhase} /> : null}
        {contextError ? <div className="customization-new-project-draft-error">{contextError}</div> : null}
        {errorMessage ? <div className="customization-new-project-draft-error">{errorMessage}</div> : null}
        <div className="customization-new-project-draft-actions">
          <Button
            type="primary"
            loading={submitting}
            disabled={!canCreateProject || submitting || Boolean(contextError) || !normalizedContent}
            onClick={handleSubmit}
          >
            {submitPhase === "generating_version" ? "正在自动生成 V1" : "提交追溯"}
          </Button>
        </div>
      </section>
    </main>
  );
}

/** 第三栏：新增定制录入区。 */
function NewCustomizationColumn({
  content,
  submitting,
  submitPhase,
  contextError,
  errorMessage,
  normalizedContent,
  disabled,
  hideHeader = false,
  onContentChange,
  onUseExample,
  onSubmit,
}: {
  content: string;
  submitting: boolean;
  submitPhase: NewProjectSubmitPhase;
  contextError: string;
  errorMessage: string;
  normalizedContent: string;
  disabled: boolean;
  hideHeader?: boolean;
  onContentChange: (value: string) => void;
  onUseExample: () => void;
  onSubmit: () => void;
}) {
  return (
    <section className="customization-new-project-column is-primary" aria-label="新增定制">
      {hideHeader ? null : (
        <div className="customization-new-project-column-header">
          <strong>新增定制</strong>
          <span>本地临时草稿</span>
        </div>
      )}
      <div className="customization-new-project-column-body is-compose">
        <NewCustomizationIntro compact />
        <div className="customization-new-project-draft-example is-compact" aria-label="本地临时参考示例">
          <div className="customization-new-project-draft-example-header">
            <span>参考示例</span>
            <Button type="text" size="mini" disabled={disabled || submitting} onClick={onUseExample}>
              填入示例
            </Button>
          </div>
          <p>{NEW_PROJECT_TRACE_EXAMPLE}</p>
        </div>
        <TextArea
          className="customization-new-project-compose-textarea"
          aria-label="需求追溯内容"
          value={content}
          autoSize={{ minRows: 6, maxRows: 12 }}
          placeholder="填写客户、老师或销售确认的第一条需求追溯"
          disabled={disabled || submitting || Boolean(contextError)}
          onChange={onContentChange}
        />
        {submitting ? <NewProjectSubmitProgress phase={submitPhase} /> : null}
        {contextError ? <div className="customization-new-project-draft-error">{contextError}</div> : null}
        {errorMessage ? <div className="customization-new-project-draft-error">{errorMessage}</div> : null}
      </div>
      <div className="customization-new-project-column-footer">
        <Button
          type="primary"
          long
          loading={submitting}
          disabled={disabled || submitting || Boolean(contextError) || !normalizedContent}
          onClick={onSubmit}
        >
          {submitPhase === "generating_version" ? "正在自动生成 V1" : "提交追溯"}
        </Button>
      </div>
    </section>
  );
}

/** 新建定制引导文案。 */
function NewCustomizationIntro({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? "customization-new-project-intro is-compact" : undefined}>
      {!compact ? <span className="customization-new-project-draft-eyebrow">本地临时草稿</span> : null}
      <h1>录入第一条需求追溯</h1>
      <Text type="secondary">
        提交有效追溯后会生成正式 JD 号、保存追溯并自动创建 V1。
      </Text>
    </div>
  );
}

/** 增定制三栏加载骨架，避免小卡片居中转圈。 */
function OpportunityBootstrapColumnSkeleton({
  title,
  lines,
  accent = false,
}: {
  title: string;
  lines: number;
  accent?: boolean;
}) {
  return (
    <section
      className={[
        "customization-new-project-column",
        "customization-new-project-bootstrap-column",
        accent ? "is-accent" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true"
    >
      <div className="customization-new-project-column-header">
        <strong>{title}</strong>
        <span className="customization-new-project-skeleton-chip" />
      </div>
      <div className="customization-new-project-column-body">
        {Array.from({ length: lines }, (_, index) => (
          <div
            key={`${title}-${index}`}
            className={`customization-new-project-skeleton-block is-line-${(index % 3) + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

/** 第一栏：按关键词搜索全部历史定制；无关键词时不请求、不展示列表。 */
function OpportunityHistoryCustomizationColumn({
  opportunityId,
  enabled,
  disabled,
  copyingId = null,
  hideHeader = false,
  onCopy,
}: {
  opportunityId: number;
  enabled: boolean;
  disabled: boolean;
  copyingId?: string | null;
  hideHeader?: boolean;
  onCopy: (item: CustomizationProjectListItem) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [items, setItems] = useState<CustomizationProjectListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const hasKeyword = enabled && Boolean(debouncedKeyword.trim());
  const countLabel = loading
    ? "搜索中"
    : !enabled
      ? "未授权"
    : hasKeyword
      ? `匹配 ${total} 个`
      : "请搜索";

  useEffect(() => {
    if (!enabled) {
      setDebouncedKeyword("");
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
    }, HISTORY_SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [enabled, keyword]);

  useEffect(() => {
    const normalized = debouncedKeyword.trim();
    if (!normalized) {
      setItems([]);
      setTotal(0);
      setErrorMessage("");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrorMessage("");
    void fetchCustomizationProjectHistory(opportunityId, {
      page: 1,
      pageSize: HISTORY_SEARCH_PAGE_SIZE,
      keyword: normalized,
    })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setItems(result.items);
        setTotal(result.total);
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMessage("历史定制搜索失败，请稍后重试。");
          setItems([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedKeyword, enabled, opportunityId]);

  return (
    <section className="customization-new-project-column" aria-label="历史定制">
      {hideHeader ? null : (
        <div className="customization-new-project-column-header">
          <strong>历史定制</strong>
          <span>{countLabel}</span>
        </div>
      )}
      <div className="customization-new-project-column-body">
        {hideHeader && enabled ? (
          <div className="customization-new-project-tab-meta" aria-live="polite">
            {countLabel}
          </div>
        ) : null}
        {enabled ? (
          <Input
            allowClear
            size="small"
            value={keyword}
            disabled={disabled}
            placeholder="联系人 / 定制号 / 项目名 / 学校 / 销售"
            aria-label="历史定制搜索"
            onChange={setKeyword}
          />
        ) : (
          <div className="customization-new-project-empty-panel">
            <strong>暂无历史搜索权限</strong>
            <p>仍可直接录入需求并创建新定制。</p>
          </div>
        )}
        {loading ? (
          <div className="customization-new-project-column-loading">
            <Spin tip="正在搜索历史定制…" size={24} />
          </div>
        ) : null}
        {errorMessage ? (
          <div className="customization-new-project-draft-error">{errorMessage}</div>
        ) : null}
        {enabled && !loading && !errorMessage && !hasKeyword ? (
          <div className="customization-new-project-empty-panel">
            <strong>先搜索再对照</strong>
            <p>输入关键词搜索全部历史定制，匹配后再复制创建。</p>
          </div>
        ) : null}
        {!loading && !errorMessage && hasKeyword && items.length === 0 ? (
          <div className="customization-new-project-empty-panel">
            <strong>未找到匹配项</strong>
            <p>可换联系人、定制号或项目名再试。</p>
          </div>
        ) : null}
        {enabled && !loading && items.length > 0 ? (
          <ul className="customization-new-project-linked-list" aria-label="历史定制列表">
            {items.map((item) => {
              const code = item.projectCode?.trim() || item.legacyId?.trim() || "--";
              const name = item.name?.trim();
              const title = name && name !== code ? name : code;
              const salesLabel = item.salesUserName?.trim() || "未配置";
              const dispatcherLabel = item.dispatcherUserName?.trim() || "未配置";
              const contactLabel = item.contactName?.trim();
              const peopleCount = resolveHistoryListPeopleCount(item);
              const factParts = [
                resolveHistoryCandidateTravelDate({
                  departureStartDate: item.departureStartDate,
                }),
                peopleCount != null ? `${peopleCount}人` : null,
                item.durationDays != null ? `${item.durationDays}天` : null,
              ].filter(Boolean);
              return (
                <li key={item.id} className="customization-new-project-linked-item is-stacked is-history">
                  <div className="customization-new-project-history-card">
                    <div className="customization-new-project-history-topline">
                      <span className="customization-new-project-history-code">
                        【定制号：<em>{code}</em>】
                      </span>
                      <span className="customization-new-project-history-dispatcher">
                        <span className="customization-new-project-history-label">计调：</span>
                        <span className="customization-new-project-history-name is-dispatcher">
                          {dispatcherLabel}
                        </span>
                      </span>
                      {item.versionNumber ? (
                        <span className="customization-new-project-history-version">
                          {item.versionNumber}
                        </span>
                      ) : null}
                    </div>
                    <strong className="customization-new-project-history-title">{title}</strong>
                    <div className="customization-new-project-history-staff">
                      <span>
                        <span className="customization-new-project-history-label">销售：</span>
                        <span className="customization-new-project-history-name is-sales">
                          {salesLabel}
                        </span>
                      </span>
                      {contactLabel ? (
                        <>
                          <span className="customization-new-project-history-sep" aria-hidden>
                            ·
                          </span>
                          <span>
                            <span className="customization-new-project-history-label">联系人：</span>
                            <span className="customization-new-project-history-name is-contact">
                              {contactLabel}
                            </span>
                          </span>
                        </>
                      ) : null}
                    </div>
                    {factParts.length > 0 ? (
                      <div className="customization-new-project-history-facts">
                        {factParts.join(" · ")}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    type="outline"
                    size="mini"
                    htmlType="button"
                    loading={copyingId === item.id}
                    disabled={disabled}
                    aria-label={`复制，创建新定制 ${title}`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onCopy(item);
                    }}
                  >
                    复制，创建新定制
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

/** 从商机进入时展示该商机已有定制，并支持按创建新定制复制已填字段。 */
function OpportunityLinkedCustomizationSection({
  items,
  errorMessage,
  disabled,
  copyingId = null,
  onCopy,
  columnMode = false,
  hideHeader = false,
}: {
  items: CustomizationOpportunityLinkedProjectItem[];
  errorMessage: string;
  disabled: boolean;
  copyingId?: string | null;
  onCopy: (project: CustomizationOpportunityLinkedProjectItem) => void;
  columnMode?: boolean;
  hideHeader?: boolean;
}) {
  const listContent = (
    <>
      {errorMessage ? (
        <div className="customization-new-project-draft-error">{errorMessage}</div>
      ) : null}
      {!errorMessage && items.length === 0 ? (
        <p className="customization-new-project-linked-empty">
          暂无关联定制，可直接录入新追溯。
        </p>
      ) : null}
      {items.length > 0 ? (
        <ul
          className="customization-new-project-linked-list"
          aria-label={items.length > 3 ? "已有定制列表，可滚动查看全部" : "已有定制列表"}
        >
          {items.map((project) => {
            const { code, name } = resolveLinkedProjectTitle(project);
            const title = name ?? code;
            const copyable = isOpportunityLinkedProjectCopyable(project);
            return (
              <li
                key={project.projectId}
                className={`customization-new-project-linked-item${
                  columnMode ? " is-stacked is-history" : ""
                }`}
              >
                {columnMode ? (
                  <div className="customization-new-project-history-card">
                    <div className="customization-new-project-history-topline">
                      <span className="customization-new-project-history-code">
                        【定制号：<em>{code}</em>】
                      </span>
                      {project.currentDemandVersionNumber ? (
                        <span className="customization-new-project-history-version">
                          {project.currentDemandVersionNumber}
                        </span>
                      ) : null}
                      {!copyable ? (
                        <span className="customization-new-project-copy-status">资料待补齐</span>
                      ) : null}
                    </div>
                    {name ? (
                      <strong className="customization-new-project-history-title">{name}</strong>
                    ) : null}
                  </div>
                ) : (
                  <div className="customization-new-project-linked-body">
                    <div className="customization-new-project-linked-title">
                      <span>{code}</span>
                      {name ? <strong>{name}</strong> : null}
                      {project.currentDemandVersionNumber ? (
                        <em>{project.currentDemandVersionNumber}</em>
                      ) : null}
                    </div>
                  </div>
                )}
                <Button
                  type="outline"
                  size="mini"
                  htmlType="button"
                  loading={copyingId === project.projectId}
                  disabled={disabled || !copyable}
                  aria-label={`${copyable ? "复制，创建新定制" : "资料待补齐"} ${title}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (copyable) {
                      onCopy(project);
                    }
                  }}
                >
                  {copyable ? "复制，创建新定制" : "资料待补齐"}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </>
  );

  if (columnMode) {
    return (
      <section className="customization-new-project-column" aria-label="本商机已有定制">
        {hideHeader ? null : (
          <div className="customization-new-project-column-header">
            <strong>本商机已有定制</strong>
            <span>共 {items.length} 个</span>
          </div>
        )}
        <div className="customization-new-project-column-body">{listContent}</div>
      </section>
    );
  }

  return (
    <section className="customization-new-project-linked" aria-label="商机已有定制">
      <div className="customization-new-project-linked-header">
        <strong>本商机已有定制</strong>
        <span>共 {items.length} 个</span>
      </div>
      {listContent}
    </section>
  );
}

/** 历史列表项人数：优先 totalCount，否则师生合计。 */
function resolveHistoryListPeopleCount(item: CustomizationProjectListItem): number | null {
  if (item.totalCount != null) {
    return item.totalCount;
  }
  if (item.studentCount == null && item.teacherCount == null) {
    return null;
  }
  return (item.studentCount ?? 0) + (item.teacherCount ?? 0);
}

/** 将目标商机资料缺失的后端错误统一成复制提示。 */
function resolveHistoryCopyErrorMessage(error: unknown): string | null {
  if (
    isHttpClientError(error) &&
    error.code === "CUSTOMIZATION_OPPORTUNITY_IDENTITY_INCOMPLETE"
  ) {
    return "需要商机设置了学校和联系人才能复制创建";
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return null;
}

/** 历史列表项映射为复制入口所需的关联定制摘要。 */
function mapHistoryListItemToLinkedProject(
  item: CustomizationProjectListItem,
  demandId: string,
  versionNumber?: string,
): OpportunityCustomizationProjectItem {
  const peopleCount = resolveHistoryListPeopleCount(item);
  return {
    projectId: item.id,
    projectCode: item.projectCode || item.legacyId,
    projectName: item.name,
    selectionStatus: "candidate",
    currentDemandId: demandId,
    effectiveDemandId: demandId,
    currentDemandVersionNumber: versionNumber ?? item.versionNumber,
    demandSummary: [
      item.contactName,
      resolveHistoryCandidateTravelDate({ departureStartDate: item.departureStartDate }),
      peopleCount != null ? `${peopleCount}人` : null,
      item.durationDays != null ? `${item.durationDays}天` : null,
      item.destinationSummary?.trim() || null,
    ]
      .filter(Boolean)
      .join(" · "),
    createdAt: item.projectCreatedAt ?? "",
    updatedAt: item.versionUpdatedAt ?? item.projectCreatedAt ?? "",
  };
}

/** 名称与编号相同时只保留编号，避免标题重复展示 JD 号。 */
function resolveLinkedProjectTitle(project: OpportunityCustomizationProjectItem): {
  code: string;
  name?: string;
} {
  const code = project.projectCode?.trim() || "--";
  const rawName = project.projectName?.trim();
  if (!rawName || rawName === code) {
    return { code };
  }
  return { code, name: rawName };
}

/** 复制创建期间的即时反馈，避免接口返回前页面无响应。 */
function CopyingProjectBanner() {
  return (
    <div className="customization-new-project-copy-busy" role="status" aria-live="polite">
      <Spin size={16} />
      <span>正在复制创建新定制…</span>
    </div>
  );
}

/** 展示新建定制提交链路的分阶段进度。 */
function NewProjectSubmitProgress({ phase }: { phase: NewProjectSubmitPhase }) {
  if (phase === "copying_project") {
    return (
      <div
        className="customization-new-project-progress"
        role="status"
        aria-live="polite"
        aria-label="新定制创建进度"
      >
        <div className="customization-new-project-progress-step is-active">
          <span className="customization-new-project-progress-dot" aria-hidden="true" />
          <div>
            <strong>正在复制创建新定制</strong>
            <span>按已有需求字段创建新项目 V1，完成后自动进入工作台。</span>
          </div>
        </div>
      </div>
    );
  }
  const traceSaved = phase === "generating_version";
  return (
    <div
      className="customization-new-project-progress"
      role="status"
      aria-live="polite"
      aria-label="新定制创建进度"
    >
      <div className={`customization-new-project-progress-step${traceSaved ? " is-complete" : " is-active"}`}>
        <span className="customization-new-project-progress-dot" aria-hidden="true" />
        <div>
          <strong>{traceSaved ? "追溯已保存" : "正在保存追溯"}</strong>
          <span>{traceSaved ? "正式 JD 号和首条追溯已创建。" : "正在创建正式项目和 JD 号。"}</span>
        </div>
      </div>
      <div className={`customization-new-project-progress-step${traceSaved ? " is-active" : ""}`}>
        <span className="customization-new-project-progress-dot" aria-hidden="true" />
        <div>
          <strong>{traceSaved ? "正在自动生成 V1" : "等待自动生成 V1"}</strong>
          <span>{traceSaved ? "模型正在整理结构化需求，完成后自动进入工作台。" : "追溯保存成功后会自动开始。"}</span>
        </div>
      </div>
    </div>
  );
}

/** 与三栏 CSS 堆叠断点对齐，窄屏改用顶栏 Tab。 */
function useIsCompactTriptychViewport(maxWidth = TRIPTYCH_TABS_MAX_WIDTH) {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const sync = () => setIsCompact(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener?.("change", sync);
    return () => mediaQuery.removeEventListener?.("change", sync);
  }, [maxWidth]);

  return isCompact;
}
